/**
 * Translating Algebraic Equations generator (verbal -> algebraic).
 *
 * Presents a verbal phrase and asks the student to pick the matching algebraic
 * expression/equation. Multiple-choice keeps grading deterministic while still
 * testing the order-sensitive language that trips students up ("five less than
 * x" is x - 5, not 5 - x). Each phrase is generated from a template with a
 * known LinearExpr, and the distractors encode the classic order/operation
 * confusions. The correct expression is verified by evaluating the LinearExpr
 * at sample points against the intended form.
 *
 * Templates (order matters!):
 *   L1: "n less than x"            -> x - n
 *   L2: "x less than n"            -> n - x
 *   L3: "the sum of x and n"       -> x + n
 *   L4: "n more than x"            -> x + n
 *   L5: "the product of n and x"   -> n x
 *   L6: "n times the sum of x and c" -> n(x + c)
 *   L7: "twice the difference of x and n" -> 2(x - n)
 *   L8: "the quotient of x and n"  -> x / n
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { LinearExpr } from "../engine/linear";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Choice,
  Question,
  QuestionSignature,
} from "./types";
import { getTopic } from "../curriculum/curriculum";

const TOPIC = "translating-equations" as const;
const VERSION = "1.0.0";
const VAR = "x";

type Template =
  | "L1" | "L2" | "L3" | "L4" | "L5" | "L6" | "L7" | "L8";

interface Cfg {
  readonly templates: readonly Template[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { templates: ["L1", "L3", "L4"], max: 9 },
  standard: { templates: ["L1", "L2", "L3", "L4", "L5"], max: 12 },
  hard: { templates: ["L2", "L5", "L6", "L7", "L8"], max: 12 },
  challenge: { templates: ["L6", "L7", "L8"], max: 12 },
};

interface Spec {
  phrase: string;
  correct: string; // LaTeX
  distractors: string[]; // LaTeX
  /** A LinearExpr the correct form represents, for verification (when linear). */
  expr?: LinearExpr;
}

function buildSpec(t: Template, n: number, c: number): Spec {
  const N = rat(n);
  const C = rat(c);
  const x = LinearExpr.variable();
  switch (t) {
    case "L1": // n less than x  -> x - n
      return {
        phrase: `${n} less than ${VAR}`,
        correct: `${VAR} - ${n}`,
        distractors: [`${n} - ${VAR}`, `${VAR} + ${n}`, `${n}${VAR}`],
        expr: x.add(LinearExpr.constantExpr(N.neg())),
      };
    case "L2": // x less than n  -> n - x
      return {
        phrase: `${VAR} less than ${n}`,
        correct: `${n} - ${VAR}`,
        distractors: [`${VAR} - ${n}`, `${VAR} + ${n}`, `\\frac{${n}}{${VAR}}`],
        expr: new LinearExpr(rat(-1), N),
      };
    case "L3": // the sum of x and n -> x + n
      return {
        phrase: `the sum of ${VAR} and ${n}`,
        correct: `${VAR} + ${n}`,
        distractors: [`${VAR} - ${n}`, `${n} - ${VAR}`, `${n}${VAR}`],
        expr: x.add(LinearExpr.constantExpr(N)),
      };
    case "L4": // n more than x -> x + n
      return {
        phrase: `${n} more than ${VAR}`,
        correct: `${VAR} + ${n}`,
        distractors: [`${VAR} - ${n}`, `${n} - ${VAR}`, `${n} \\cdot ${VAR}`],
        expr: x.add(LinearExpr.constantExpr(N)),
      };
    case "L5": // the product of n and x -> n x
      return {
        phrase: `the product of ${n} and ${VAR}`,
        correct: `${n}${VAR}`,
        distractors: [`${VAR} + ${n}`, `\\frac{${VAR}}{${n}}`, `${VAR} - ${n}`],
        expr: new LinearExpr(N, rat(0)),
      };
    case "L6": // n times the sum of x and c -> n(x + c)
      return {
        phrase: `${n} times the sum of ${VAR} and ${c}`,
        correct: `${n}(${VAR} + ${c})`,
        distractors: [`${n}${VAR} + ${c}`, `${n} + ${VAR} + ${c}`, `${n}(${VAR} - ${c})`],
        expr: x.add(LinearExpr.constantExpr(C)).scale(N),
      };
    case "L7": // twice the difference of x and n -> 2(x - n)
      return {
        phrase: `twice the difference of ${VAR} and ${n}`,
        correct: `2(${VAR} - ${n})`,
        distractors: [`2${VAR} - ${n}`, `2(${n} - ${VAR})`, `${VAR} - 2${n}`],
        expr: x.add(LinearExpr.constantExpr(N.neg())).scale(rat(2)),
      };
    case "L8": // the quotient of x and n -> x / n
      return {
        phrase: `the quotient of ${VAR} and ${n}`,
        correct: `\\frac{${VAR}}{${n}}`,
        distractors: [`\\frac{${n}}{${VAR}}`, `${n}${VAR}`, `${VAR} - ${n}`],
        expr: new LinearExpr(rat(1, n), rat(0)),
      };
  }
}

/** Verify a linear spec's expr matches its intended value at sample points. */
function verifyLinear(spec: Spec, checks: Array<[number, Rational]>): boolean {
  if (!spec.expr) return true; // non-linear (e.g. product/quotient) trusted from table
  for (const [x, expected] of checks) {
    if (!spec.expr.evaluate(rat(x)).equals(expected)) return false;
  }
  return true;
}

export class TranslatingEquationsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = ["easy", "standard", "hard", "challenge"];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const t = rng.pick(cfg.templates);
      const n = rng.int(2, cfg.max);
      const c = rng.int(2, cfg.max);
      const spec = buildSpec(t, n, c);

      // Independent verification for the linear templates via substitution.
      const ok = verifyLinear(spec, [
        [0, evalIntended(t, 0, n, c)],
        [3, evalIntended(t, 3, n, c)],
      ]);
      if (!ok) continue;

      const correctChoice: Choice = { id: "correct", latex: spec.correct };
      const distractors: Choice[] = spec.distractors.map((d, i) => ({ id: `d${i}`, latex: d }));
      const choices = rng.shuffle([correctChoice, ...distractors]);

      return {
        id: `${TOPIC}-${ctx.seed}-${t}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family: t,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Which expression means: "${spec.phrase}"?`,
          latex: `\\text{Which expression means: “${spec.phrase}”?}`,
        },
        answerType: "choice",
        answer: { kind: "choice", value: "correct" },
        choices,
        steps: [
          {
            description: "Translate the phrase, paying attention to word order.",
            latex: spec.correct,
          },
        ],
        hints: [
          { level: 1, text: "Read carefully: order matters for subtraction and division." },
          { level: 2, text: `"less than" and "difference" reverse the order you might expect.` },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure: q.family };
  }
}

/** The intended numeric value of the correct expression at x, for verification. */
function evalIntended(t: Template, x: number, n: number, c: number): Rational {
  switch (t) {
    case "L1":
      return rat(x - n);
    case "L2":
      return rat(n - x);
    case "L3":
    case "L4":
      return rat(x + n);
    case "L5":
      return rat(n * x);
    case "L6":
      return rat(n * (x + c));
    case "L7":
      return rat(2 * (x - n));
    case "L8":
      return rat(x, n);
  }
}

export const translatingEquations = new TranslatingEquationsGenerator();
